import React, {Component} from 'react';
import styled from 'styled-components';
import {SortableHandle} from 'react-sortable-hoc';

import {areShallowEqual} from '../../lib/helpers';
import {parseVariables} from '../../lib/json-logic-utils';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import JsonLogicBuilder from '../../components/JsonLogicBuilder';
import AlertDialog from '../../components/AlertDialog';
import Button from '../../components/Button';
import Menu from '../../components/Menu';

import {
  createQuestionOption,
  getLogicBuilderVariables,
  hasChoices,
  createQuestion,
  MEASUREMENT_UNITS,
  getMeasurementUnit,
  getScaleTarget,
  WidthChangeContainer,
  SegmentOption,
  SegmentedControl,
  WidthWarning,
  WidthLabel,
  isFormulaDefaultAnswer,
  isSequentialCodeQuestion,
  FORMULA_DEFAULT_ANSWERS_ENABLED
} from './shared';
import DeletableCard from './DeletableCard';
import SubConfiguration from './SubConfiguration';
import OptionsBuilder from './OptionsBuilder';
import DefaultAnswers from './DefaultAnswers';
import { isValidCssColor } from '../../lib/color';

import NumericGridBuilder from './NumericGridBuilder';
import CodeGeneratorConfig from './CodeGeneratorConfig';
import * as flags from "../../lib/flags";
import {withUser} from "../../lib/user";

// Formula default answers are held back by FORMULA_DEFAULT_ANSWERS_ENABLED, so until that is turned on a
// stored formula is treated as a plain default answer.
function hasFormulaDefaultAnswer(defaultAnswers) {
  return FORMULA_DEFAULT_ANSWERS_ENABLED && isFormulaDefaultAnswer(defaultAnswers);
}

class QuestionBuilder extends Component {
  state = {
    editCondition: !!this.props.question.condition,
    editDefaultAnswers: this.props.question.defaultAnswers.length > 0,
    restrictCatalogTypes: getQuestionMetadata(this.props.question, 'selectedTypes').length > 0,
    validationEnabled: false,
    dialog: null,
    optionsOpen: false,
    showAdvancedSettings: hasAdvancedSettings(this.props.question),
    allowSaveAnswers: getQuestionMetadata(this.props.question, 'allowSaveAnswers').includes('false') ? false : true,
    gridWidth: parseInt(getQuestionMetadata(this.props.question, 'gridWidth')[0]) || 2,
    gridHeight: parseInt(getQuestionMetadata(this.props.question, 'gridHeight')[0]) || 2,
    displayInline: getQuestionMetadata(this.props.question, 'displayInline').includes('true'),
    questionWidth: getQuestionMetadata(this.props.question, 'questionWidth')[0] || '1',
    questionColour: getQuestionMetadata(this.props.question, 'questionColour')[0] || '#000000',
    cardColour: getQuestionMetadata(this.props.question, 'cardColour')[0] || '#FFFFFF',
    deleted: this.props.question.deleted || false,
    // Formula default answers are always locked.
    lockDefault: this.props.question.lockDefault || hasFormulaDefaultAnswer(this.props.question.defaultAnswers),
    allowUserOptions: getQuestionMetadata(this.props.question, 'allowUserOptions').includes('true') ? true : false,
    allowLibraryPhotos: getQuestionMetadata(this.props.question, 'allowLibraryPhotos').includes('false') ? false : true,
  };

  shouldComponentUpdate(prevProps, prevState) {
    const {question, disableDelete} = this.props;
    
    if (prevState !== this.state) {
      // Always update when the local state is updated.
      return true;
    }

    if (prevProps.disableDelete !== disableDelete) {
      // Always update when the disableDelete prop is changed.
      return true;
    }

    if (prevProps.question !== question) {
      // Always update when the question prop is changed.
      return true;
    }

    // Checking validations is more involved because you need to check the entire object.
    const {questionOptionsByValue: prevQuestionOptionsByValue, ...prevValidations} = prevProps.validations;
    const {validations: {questionOptionsByValue, ...validations}} = this.props;

    if (!areShallowEqual(prevValidations, validations) || !areShallowEqual(prevQuestionOptionsByValue, questionOptionsByValue)) {
      // Always update if the validations have changed.
      return true;
    }

    return false;
  }

  componentDidMount() {
    this.resetInlineIfFirstQuestion();
  }

  componentDidUpdate() {
    this.resetInlineIfFirstQuestion();
  }

  // clear display inline whenever a question is moved to order = 0
  resetInlineIfFirstQuestion = () => {
    const {question} = this.props;
    if (question.order === 0 && this.state.displayInline) {
      this.setState({displayInline: false});
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'displayInline')
      }));
    }
  }

  closeAlertDialog = () => {
    this.setState({dialog: null});
  }

  openAlertDialog = props => {
    this.setState({dialog: props});
  }

  handleConfirmDelete = () => {
    const {form, question} = this.props;

    // Deleting the question is not allowed if this question is referenced in a condition or alert expression.
    const dependentQuestions = getDependentQuestions(form, question);

    if (dependentQuestions.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the condition for the "${dependentQuestions[0].text}" question.`
      });

      return false;
    }

    const dependentAlerts = getDependentAlerts(form, question);

    if (dependentAlerts.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the condition for the "${dependentAlerts[0].name}" alert.`
      });

      return false;
    }

    const dependentContextAlerts = getDependentContextAlerts(form, question);

    if(dependentContextAlerts.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the condition for the "${dependentContextAlerts[0].name}" launch alert.`
      });

      return false;
    }

    const dependentElements = getDependentElements(form, question);

    if (dependentElements.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the "${dependentElements[0].data.label}" element.`
      });

      return false;
    }

    const dependentNotifications = getDependentNotifications(form, question);
    if (dependentNotifications.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the condition for a notification with user: ${dependentNotifications[0].user.userName}.`
      });
      return false;
    }

    if(question.context && form.published) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `Published setup questions can not be deleted!`
      });

      return false;
    }

    return true;
  }

  handleRemove = (newQuestion) => {
    const {question, onRemove} = this.props;
    onRemove(question, newQuestion);
  }

  setQuestionState = (reducer, onComplete) => {
    const {question, onChange} = this.props;

    onChange({
      ...question,
      ...(typeof reducer === 'function' ? reducer(question) : reducer)
    }, onComplete);
  }

  handleQuestionTypeChange = e => {
    const {form, question} = this.props;

    // Changing the question type is not allowed if this question is referenced in a condition or alert expression.
    const dependentQuestions = getDependentQuestions(form, question);

    if (dependentQuestions.length > 0) {
      return this.openAlertDialog({
        title: `Can't change question type!`,
        description: `This question is used by the condition for the "${dependentQuestions[0].text}" question.`
      });
    }

    const dependentAlerts = getDependentAlerts(form, question);

    if (dependentAlerts.length > 0) {
      return this.openAlertDialog({
        title: `Can't change question type!`,
        description: `This question is used by the condition for the "${dependentAlerts[0].name}" alert.`
      });
    }

    const dependentNotifications = getDependentNotifications(form, question);
    if (dependentNotifications.length > 0) {
      return this.openAlertDialog({
        title: `Can't change question type!`,
        description: `This question is used by the condition for a notification with user ${dependentNotifications[0].user.userName}.`
      });
    }


    // Calculate the next question options state.
    let questionOptions = question.questionOptions;

    if (!hasChoices(e.target.value)) {
      questionOptions = [];
    }
    else if (questionOptions.length < 1) {
      questionOptions = [createQuestionOption('', 0)];
    }

    const newType = e.target.value;
    const resetContext = (newType === "MULTI_NUMERIC" || newType === "NUMERIC_GRID")
      ? { context: false, preview: false, previewOrder: 0 }
      : {};

    const typeSpecificMeta = {
      measurementUnit: ['MEASURED_NUMERIC'],
      scaleTarget: ['MEASURED_NUMERIC'],
      gridWidth: ['NUMERIC_GRID'],
      gridHeight: ['NUMERIC_GRID'],
      displayHorizontal: ['RADIO'],
      selectedTypes: ['CATALOG_RADIO', 'CATALOG_CHECKBOX'],
      codeType: ['CODE_GENERATOR'],
      codePrefix: ['CODE_GENERATOR'],
      codeSuffix: ['CODE_GENERATOR'],
      codeFormat: ['CODE_GENERATOR'],
      codeSeqStart: ['CODE_GENERATOR'],
      codeSeqPadding: ['CODE_GENERATOR']
    };

    // reset the grid to 2,2
    if (newType === 'NUMERIC_GRID') {
      this.setState({gridWidth: 2, gridHeight: 2});
    }

    // if swapping from catalog to other types turn the restrict flag off
    if (isCatalogQuestion(newType) && !isCatalogQuestion(question.type)) {
      this.setState({restrictCatalogTypes: false});
    }

    this.setQuestionState(question => ({
      type: newType,
      defaultAnswers: [],
      questionOptions,
      questionMeta: question.questionMeta.filter(meta =>
        !typeSpecificMeta[meta.key] || typeSpecificMeta[meta.key].includes(newType)
      ), // remove type-specific metadata when types are changed
      ...resetContext
    }));
  }

  handleRequiredChanged = e => {
    this.setQuestionState({required: e.target.checked, defaultAnswers: []});
  }

  handleConditionToggle = () => {
    const {editCondition} = this.state;

    if (editCondition) {
      this.setQuestionState({condition: null});
    }

    this.setState({editCondition: !editCondition});
  }

  handleOptionsChanged = (options, onComplete) => {
    this.setQuestionState({questionOptions: options}, onComplete);
  }

  handleDefaultAnswersChanged = defaultAnswers => {
    if (hasFormulaDefaultAnswer(defaultAnswers)) {
      // A formula default answer is worked out for the user, so it is always locked.
      this.setState({lockDefault: true});
      this.setQuestionState({defaultAnswers, lockDefault: true});
      return;
    }

    this.setQuestionState({defaultAnswers});
  }

  handleLockDefaultChanged = value => {
    this.setState({lockDefault: value});
    this.setQuestionState({lockDefault: value})
  }

  handleSetDefaultAnswer = e => {
    this.setState({editDefaultAnswers: e.target.checked});

    if (!e.target.checked) {
      this.handleLockDefaultChanged(false);
      this.handleDefaultAnswersChanged([]);
    }
  }

  handleSetCatalogTypes = e => {
    this.setState({restrictCatalogTypes: e.target.checked});

    if (!e.target.checked) {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'selectedTypes')
      }));
    }
  }

  handleAddCatalogType = (e, type) => {
    if (e.target.checked) {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta, {key: 'selectedTypes', value: type.id}]
      }));
    }
    else {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta
          .filter(metadata => metadata.key !== 'selectedTypes' || metadata.value !== type.id)
      }));
    }
  }

  toggleQuestionOptions = () => {
    this.setState(prevState => ({
      optionsOpen: !prevState.optionsOpen
    }));
  }

  handleCopyQuestion = () => {
    const {question, onAdd} = this.props;
    onAdd({...question, text: `Copy of ${question.text}`});
  }

  handleAddQuestionAbove = () => {
    const {question, onAdd} = this.props;
    onAdd(createQuestion(question.order - 1));
  }

  handleAddQuestionBelow = () => {
    const {question, onAdd} = this.props;
    onAdd(createQuestion(question.order));
  }

  toggleAdvancedSettings = () => {
    this.setState(prevState => ({
      showAdvancedSettings: !prevState.showAdvancedSettings
    }));
  }

  handleAllowSaveAnswersToggle = e => {
    const checked = e.target.checked;
    this.setState({allowSaveAnswers: checked});

    if (checked) {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'allowSaveAnswers')
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta, {key: 'allowSaveAnswers', value: 'false'}]
      }));
    }
  }

  // Allows users on the mobile side to add options to pick lists at answer time
  handleAllowUserOptionsToggle = e => {
    const checked = e.target.checked;
    this.setState({allowUserOptions: checked});

    if (checked) {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta, {key: 'allowUserOptions', value: 'true'}]
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta, {key: 'allowUserOptions', value: 'false'}]
      }));
    }

  }

  // Allows users on the mobile side to pick photos from their device library instead of only the camera.
  // Allowed by default; store 'false' only when explicitly restricted to camera-only.
  handleAllowLibraryPhotosToggle = e => {
    const checked = e.target.checked;
    this.setState({allowLibraryPhotos: checked});

    if (checked) {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'allowLibraryPhotos')
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'allowLibraryPhotos'), {key: 'allowLibraryPhotos', value: 'false'}]
      }));
    }
  }

  handleMeasurementUnitChange = e => {
    const value = e.target.value;
    this.setQuestionState(question => ({
      questionMeta: value
        ? [...question.questionMeta.filter(metadata => metadata.key !== 'measurementUnit'), {key: 'measurementUnit', value}]
        : question.questionMeta.filter(metadata => metadata.key !== 'measurementUnit')
    }));
  }

  // A question shown on setup is answered once for the whole sheet rather than by each weighing, so the scale has
  // nothing to fill it with. Moving the scale target onto setup therefore takes the scale target off it.
  handleContextChange = e => {
    const checked = e.target.checked;
    this.setQuestionState(question => ({
      context: checked,
      ...(!checked && {preview: false, previewOrder: 0}),
      ...(checked && getScaleTarget(question) && {
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'scaleTarget')
      })
    }));
  }

  handleScaleTargetChange = e => {
    const checked = e.target.checked;
    this.setQuestionState(question => ({
      questionMeta: [
        ...question.questionMeta.filter(metadata => metadata.key !== 'scaleTarget'),
        {key: 'scaleTarget', value: checked ? 'true' : 'false'}
      ]
    }));
  }

  handleCodeMetaChange = updates => {
    this.setQuestionState(question => {
      let questionMeta = question.questionMeta;

      Object.keys(updates).forEach(key => {
        const value = updates[key];
        questionMeta = questionMeta.filter(metadata => metadata.key !== key);

        if (value !== null && value !== undefined && value !== '') {
          questionMeta = [...questionMeta, {key, value}];
        }
      });

      //the api turns a condition down outright, so switching to sequential has to clear it here rather
      //than let the save come back as just an empty 400 with nothing marked on the form. a number is
      //taken whenever the form is saved, whether or not a condition would have hidden the question
      const sequentialRestrictions = updates.codeType === 'SEQUENTIAL'
        ? {condition: null}
        : {};

      return {questionMeta, ...sequentialRestrictions};
    });

    if (updates.codeType === 'SEQUENTIAL') {
      this.setState({editCondition: false});
    }
  }

  handleGridConfigChange = ({width, height}) => {
    this.setState({gridWidth: width, gridHeight: height});
    this.setQuestionState(question => ({
      questionMeta: [
        ...question.questionMeta.filter(metadata => metadata.key !== 'gridWidth' && metadata.key !== 'gridHeight'),
        {key: 'gridWidth', value: width.toString()},
        {key: 'gridHeight', value: height.toString()}
      ]
    }));
  }

  handleDisplayInlineToggle = e => {
    const checked = e.target.checked;
    this.setState({displayInline: checked});

    if (checked) {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'displayInline'), {key: 'displayInline', value: 'true'}]
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'displayInline')
      }));
    }
  }

  handleQuestionWidthChange = e => {
    const value = e.target.value;
    this.setState({questionWidth: value});

    if (value === '1' || value === '1.0') {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'questionWidth')
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'questionWidth'), {key: 'questionWidth', value}]
      }));
    }
  }

  handleQuestionColourChange = e =>{
    const value = e; 
    this.setState({questionColour: value});

    if (value === '#000000') { //base case Default Color
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'questionColour')
      }))
    }
    else{ //Change color 
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'questionColour'), {key: 'questionColour',value}]
      }))
    }
  }

  //handler function for the card colour
  handleCardColourChange = e => {
    const value = e;
    this.setState({cardColour: value});

    if (value === '#FFFFFF' || value === '#ffffff') {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'cardColour')
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'cardColour'), {key: 'cardColour', value}]
      }));
    }
  }

  render() {
    const {form, catalog, catalogTypes, question, validations, originalQuestionList, user} = this.props;
    const {editCondition, restrictCatalogTypes, editDefaultAnswers, dialog, optionsOpen, showAdvancedSettings, allowSaveAnswers, gridWidth, gridHeight, displayInline, questionWidth, lockDefault, allowUserOptions, allowLibraryPhotos} = this.state;
    const selectedCatalogTypes = getQuestionMetadata(question, 'selectedTypes'); // Only used for single and multi-catalog questions.
    const isFirstQuestion = question.order === 0;
    const formulaDefaultAnswer = hasFormulaDefaultAnswer(question.defaultAnswers);
    const newQuestion = !originalQuestionList.some(q => q.id === question.id);
    //Create New question variable that knows if it's a newly created question, check if this question is in the original list
    const scaleFlag = flags.isOn(user, 'SCALE_PRINT_ENABLED');
    return (
      <DeletableCard
        focusable
        form={form}
        dialogTitle={(form.published && !newQuestion) ? "Delete this question from the published form?" : "Delete this question?"}
        dialogDescription= {(form.published && !newQuestion) ? 'Are you sure you want to do delete this question?' +
            '\nUsers will no longer be able to answer this question, but existing answers will be preserved.'
            : "Are you sure you want to delete this question? This cannot be undone."}
        ok="Delete"
        onBeforeOk={this.handleConfirmDelete}
        onOk={()=> this.handleRemove(newQuestion)}
        //added newQuestion here to ensure the question being deleted won't affect database records
        overlay={dialog && <AlertDialog onOk={this.closeAlertDialog} {...dialog} />}
        newQuestion={newQuestion}
      >
        <Header>
          <DragIcon>
            <Icon name="align-justify" size="1.5em" />
          </DragIcon>
          <HeaderContent>
            {!form.published || newQuestion ? // Unable to edit Question names if published
                <QuestionTitleInput
                    type="text"
                    placeholder="Question Title"
                    value={question.text}
                    onChange={e => this.setQuestionState({text: e.target.value})}
                    invalid={validations.text}
                    validationText={validations.text}
                />
                :
                <h4>{question.text}</h4>
            }
            {question.type === 'NUMERIC_GRID' && (
              <QuestionSubtitle>
                A multi number question formatted as a grid
              </QuestionSubtitle>
            )}
          </HeaderContent>
          <Input
            disabled={form.published && !newQuestion} //Allow inputs if it's a newly made question
            type="select" 
            value={question.type}
            onChange={this.handleQuestionTypeChange}
            invalid={validations.type}
            validationText={validations.type || ""}>
            <option value="">Question Type</option>
            <option value="TEXT">Text</option>
            <option value="NUMERIC">Number</option>
            <option value="MEASURED_NUMERIC">Measured Number</option>
            <option value="MULTI_NUMERIC">Multi-number</option>
            <option value="NUMERIC_GRID">Number Grid</option>
            <option value="RADIO">Single-select</option>
            <option value="CHECKBOXES">Multi-select</option>
            <option value="BOOLEAN">Yes or No</option>
            <option value="IMAGE">Photo</option>
            <option value="DATETIME">Date &amp; Time</option>
            <option value="SIGNATURE">Signature</option>
            <option value="SCANNED_CODE">Code Scanner</option>
            <option value="CODE_GENERATOR">Code Generator</option>
            {catalog.length > 0 && (
              <>
                <option value="CATALOG_RADIO">Single-catalog</option>
                <option value="CATALOG_CHECKBOX">Multi-catalog</option>
              </>
            )}
          </Input>
            <>
              <QuestionOptionsButton id={`question-options-${question.id}`} tertiary onClick={this.toggleQuestionOptions}>
                <Icon name="chevron-down" />
              </QuestionOptionsButton>
              <Menu open={optionsOpen} relativeTo={`question-options-${question.id}`} onClose={this.toggleQuestionOptions}>
                <Menu.Option onClick={this.handleCopyQuestion}>Copy question</Menu.Option>
                <Menu.Option onClick={this.handleAddQuestionAbove}>Add question above</Menu.Option>
                <Menu.Option onClick={this.handleAddQuestionBelow}>Add question below</Menu.Option>
              </Menu>
            </>
        </Header>
        <Body>
          {hasChoices(question.type) && (
            <>
              <OptionsBuilder
                question={question}
                validations={validations.questionOptionsByValue}
                onChange={this.handleOptionsChanged}
                onChangeDefaultAnswers={this.handleDefaultAnswersChanged}
              />
            </>
          )}
          {question.type === 'NUMERIC_GRID' && (
            <NumericGridBuilder
              gridWidth={gridWidth}
              gridHeight={gridHeight}
              questionText={question.text}
              onChange={this.handleGridConfigChange}
            />
          )}
          {question.type === 'MEASURED_NUMERIC' && (
            <MeasurementUnitRow>
              <Input
                id={`measurement-unit-${question.id}`}
                type="select"
                label="Measurement unit"
                value={getMeasurementUnit(question) || ''}
                onChange={this.handleMeasurementUnitChange}
                invalid={!getMeasurementUnit(question)}
                validationText={!getMeasurementUnit(question) ? 'A measurement unit is required' : ''}>
                <option value="">Choose a unit</option>
                <optgroup label="Weight">
                  {MEASUREMENT_UNITS.filter(u => u.category === 'WEIGHT').map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Volume">
                  {MEASUREMENT_UNITS.filter(u => u.category === 'VOLUME').map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </optgroup>
              </Input>
              {/* Not offered on a question shown on setup, unless one already is the scale target - it has to be
                  possible to untick it. */}
              {scaleFlag && (!question.context || getScaleTarget(question)) && ( <Checkbox invalid={!!validations.scaleTarget} validationText={validations.scaleTarget}>
                <input
                  type="checkbox"
                  checked={getScaleTarget(question)}
                  onChange={this.handleScaleTargetChange}
                />
                &nbsp;
                Use as scale target
              </Checkbox> )}
            </MeasurementUnitRow>
          )}
          {question.type === 'CODE_GENERATOR' && (
            <CodeGeneratorConfig
              question={question}
              validations={validations.codeGenerator || {}}
              disabled={form.published && !newQuestion}
              onMetaChange={this.handleCodeMetaChange}
            />
          )}
          {isCatalogQuestion(question.type) && (
            <>
              <Checkbox>
                <input
                  type="checkbox"
                  checked={restrictCatalogTypes}
                  onChange={this.handleSetCatalogTypes}
                />
                &nbsp;
                Restrict the available catalog types
              </Checkbox>
              {restrictCatalogTypes && (
                <SubConfiguration>
                  {catalogTypes.map(type => (
                    <SubConfiguration.Item key={type.id}>
                      <Checkbox>
                        <input
                          type="checkbox"
                          checked={selectedCatalogTypes.indexOf(type.id) >= 0}
                          onChange={e => this.handleAddCatalogType(e, type)}
                        />
                        &nbsp;
                        {type.name}
                      </Checkbox>
                    </SubConfiguration.Item>
                  ))}
                </SubConfiguration>
              )}
            </>
          )}
          {question.type === 'IMAGE' && (
            <Checkbox>
              <input
                type="checkbox"
                checked={allowLibraryPhotos}
                onChange={this.handleAllowLibraryPhotosToggle}
              />
              &nbsp;
              Allow photos from device library
            </Checkbox>
          )}
          <Checkbox>
            <input
              type="checkbox"
              checked={question.required}
              onChange={this.handleRequiredChanged}
            />
            &nbsp;
            This question is required
          </Checkbox>
          <Checkbox>
            <input
              disabled={form.published || question.type === "MULTI_NUMERIC" || question.type === "NUMERIC_GRID"}
              type="checkbox"
              checked={question.context}
              onChange={this.handleContextChange}
            />
            &nbsp;
            Show on setup
          </Checkbox>
          {question.context && question.type!=="IMAGE" && question.type!=="SIGNATURE" && question.type!=="SCANNED_CODE" && <Checkbox>
            <input
                type="checkbox"
                checked={question.preview}
                onChange={e => this.setQuestionState({preview: e.target.checked})}
            />
            &nbsp;
            Set as Preview Question
          </Checkbox>}
          {question.preview && ( <PreviewOrderContainer>
            Set Preview Order:
              <Input type="number"
                     name="previewOrder"
                     value={question.previewOrder || 0}
                     onChange={e => this.setQuestionState({previewOrder: parseInt(e.target.value)})}/>
          </PreviewOrderContainer>)}
          <AdvancedSettingsToggle onClick={this.toggleAdvancedSettings}>
            <Icon name="chevron-right" style={{transform: showAdvancedSettings ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}} />
            &nbsp;
            Advanced Settings
          </AdvancedSettingsToggle>
          {showAdvancedSettings && (
            <AdvancedSettingsSection>
              {isPicklistQuestion(question) && (
                  <Checkbox>
                    <input
                        type="checkbox"
                        checked={allowUserOptions}
                        onChange={e => this.handleAllowUserOptionsToggle(e)}
                    />
                    &nbsp;
                    Allow users to add pick list options
                  </Checkbox>
              )}
              {canSetDefaultAnswer(question) && !hasChoices(question.type) && (
                <>
                  <Checkbox>
                    <input
                      type="checkbox"
                      checked={editDefaultAnswers}
                      onChange={this.handleSetDefaultAnswer}
                    />
                    &nbsp;
                    Set a default answer
                  </Checkbox>
                  {editDefaultAnswers && (
                    <Checkbox>
                      <input
                        type="checkbox"
                        checked={lockDefault}
                        disabled={formulaDefaultAnswer}
                        onChange={(e) => this.handleLockDefaultChanged(e.target.checked)}
                      />
                      &nbsp;
                      Lock default answer
                    </Checkbox>
                    )}
                  {editDefaultAnswers && (
                    <DefaultAnswers
                      form={form}
                      catalog={catalog}
                      question={question}
                      onChange={this.handleDefaultAnswersChanged}
                    />
                  )}
                </>
              )}
              <Checkbox>
                <input
                  type="checkbox"
                  disabled={isSequentialCodeQuestion(question)}
                  checked={editCondition}
                  onChange={this.handleConditionToggle}
                />
                &nbsp;
                Only ask this question if...
              </Checkbox>
              {editCondition && (
                <SubConfiguration>
                  <SubConfiguration.Item>
                    <JsonLogicBuilder
                      expand
                      variables={getLogicBuilderVariables(form, catalog, question)}
                      value={typeof question.condition === 'string' ? JSON.parse(question.condition) : null}
                      onChange={condition => this.setQuestionState({condition: JSON.stringify(condition)})}
                    />
                  </SubConfiguration.Item>
                </SubConfiguration>
              )}
              <Checkbox>
                <input
                  type="checkbox"
                  checked={allowSaveAnswers}
                  onChange={this.handleAllowSaveAnswersToggle}
                />
                &nbsp;
                Allow answers to be saved
              </Checkbox>
              {question.type === 'RADIO' && (
                <Checkbox>
                  <input
                    type="checkbox"
                    checked={getQuestionMetadata(question, 'displayHorizontal').includes('true')}
                    onChange={e => {
                      const checked = e.target.checked;
                      this.setQuestionState(question => ({
                        questionMeta: checked
                          ? [...question.questionMeta.filter(metadata => metadata.key !== 'displayHorizontal'),
                             {key: 'displayHorizontal', value: 'true'}]
                          : question.questionMeta.filter(metadata => metadata.key !== 'displayHorizontal')
                      }));
                    }}
                  />
                  &nbsp;
                  Display Horizontal
                </Checkbox>
              )}
              {!isFirstQuestion && question.type !== 'NUMERIC_GRID' && (
                <Checkbox>
                  <input
                    type="checkbox"
                    checked={displayInline}
                    onChange={this.handleDisplayInlineToggle}
                  />
                  &nbsp;
                  Display inline with previous question/element
                </Checkbox>
              )}
              {question.type !== 'NUMERIC_GRID' && (
                <WidthChangeContainer>
                  <div>
                    <WidthLabel>Question width:</WidthLabel>
                    <SegmentedControl>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '1' || questionWidth === '1.0'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '1'}})}
                    >
                      Full
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.75'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.75'}})}
                    >
                      3/4
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.67' || questionWidth === '0.66'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.67'}})}
                    >
                      2/3
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.5'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.5'}})}
                    >
                      1/2
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.33'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.33'}})}
                    >
                      1/3
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.25'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.25'}})}
                    >
                      1/4
                    </SegmentOption>
                    </SegmentedControl>
                  </div>
                  {parseFloat(questionWidth) < 0.5 && (
                    <WidthWarning>
                      Questions narrower than 1/2 width will display at 1/2 width on phones
                    </WidthWarning>
                  )}
                </WidthChangeContainer>
              )}
              <ColorPickerGroup>
                <Input
                  id='backgroud-color-picker'
                  label='Question Color:'
                  labelPosition='start'
                  color={this.state.questionColour || "#000000"}
                  type='color'
                  invalid={!isValidCssColor(this.state.questionColour || "#000000")}
                  onChange={this.handleQuestionColourChange}
                />
                <Input
                  id='card-colour-picker'
                  label='Question Background Color:'
                  labelPosition='start'
                  color={this.state.cardColour || "#ffffff"}
                  type='color'
                  invalid={!isValidCssColor(this.state.cardColour || "#ffffff")}
                  onChange={this.handleCardColourChange}
                />
              </ColorPickerGroup>
              {(this.state.questionColour || '#000000').toLowerCase() === (this.state.cardColour || '#FFFFFF').toLowerCase() && (
                <ColorWarning>
                  <Icon name="alert-triangle" size="1em" />
                  &nbsp;
                  Warning: Question color and background color are the same. Question text may not be visible.
                </ColorWarning>
              )}
            </AdvancedSettingsSection>
          )}
        </Body>
      </DeletableCard>
    );
  }
}

function canSetDefaultAnswer(question) {
  return !question.required
    && (question.type === 'NUMERIC'
    || question.type === 'MEASURED_NUMERIC'
    || question.type === 'TEXT'
    || question.type === 'RADIO'
    || question.type === 'CHECKBOXES'
    || question.type === 'BOOLEAN');
}

//helper function to check if components in the advanced settings are not their default values
function hasAdvancedSettings(question) {
  const meta = key => getQuestionMetadata(question, key);
  return !!question.condition
    || question.defaultAnswers.length > 0
    || meta('allowSaveAnswers').includes('false')
    || meta('displayInline').includes('true')
    || (meta('questionWidth')[0] && meta('questionWidth')[0] !== '1' && meta('questionWidth')[0] !== '1.0')
    || (meta('questionColour')[0] && meta('questionColour')[0] !== '#000000')
    || (meta('cardColour')[0] && meta('cardColour')[0] !== '#FFFFFF' && meta('cardColour')[0] !== '#ffffff')
    || meta('displayHorizontal').includes('true');
}

function isCatalogQuestion(questionType) {
  return questionType === 'CATALOG_RADIO' || questionType === 'CATALOG_CHECKBOX';
}

function getDependentQuestions(form, question) {
  // Returns the questions from the form that depend on this question.
  const questionsWithConditions = [
    ...form.contextQuestions.filter(q => !!q.condition),
    ...form.questions.filter(q => !!q.condition)
  ].sort((a, b) => a.order - b.order);

  return questionsWithConditions
    .filter(q => parseVariables(JSON.parse(q.condition)).indexOf(question.id) >= 0);
}

function getDependentAlerts(form, question) {
  // Returns the alerts from the form that depend on this question.
  return form.alerts
    .filter(alert => parseVariables(JSON.parse(alert.expression)).indexOf(question.id) >= 0);
}

function getDependentContextAlerts(form, question) {
  // Returns the launch alerts from the form that depend on this question.
  return form.contextAlerts
    .filter(alert => parseVariables(JSON.parse(alert.expression)).indexOf(question.id) >= 0);
}

function getDependentNotifications(form, question) {
  // Returns the notifications from the form that depend on this question.
  const notifications = [form.rowApprovedContacts, form.rowRejectedContacts, form.rowCompleteContacts];
  return notifications
      .flat()
      .filter(notification =>
          parseVariables(JSON.parse(notification.expression))
              .includes(question.id)
      );
}

function getDependentElements(form, question) {
  return form.elements
    .filter(element => element.type === 'CALCULATION' || element.type === 'AGGREGATION')
    .filter(element => {
        if (!element.data || !element.data.formula) return false;
        return parseVariables(JSON.parse(element.data.formula)).includes(question.id);
    });
}

function getQuestionMetadata(question, key) {
  return question.questionMeta
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value);
}

function isPicklistQuestion(question) {
  return question.type === "RADIO" || question.type === "CHECKBOXES";
}

const Header = styled.div`
  display: flex;

  h4 {
    flex: 1;
    margin: 0.4rem 0 0 0;
    padding: 0;
    font-size: 1.125rem;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const QuestionSubtitle = styled.p`
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0;
`;

const DragIcon = SortableHandle(styled.div`
  cursor: grab;
  color: #b4b4b4;
  margin-top: 0.4rem;
  margin-right: 1rem;
`);

const QuestionTitleInput = styled(({className, ...inputProps}) => (
  <div className={className}>
    <Input {...inputProps} />
  </div>
))`
  flex: 1;
  padding: 0 1rem 0 0;
`;

const QuestionOptionsButton = styled(Button)`
  margin-right: -1rem;
  margin-left: 1rem;
`;

const Body = styled.div`
  margin: 1rem 0 0 2.5rem;

  > * {
    margin-top: 0.625rem;
  }
`;

// Renders text beneath a checkbox with validation errors
const CheckboxHelp = styled.small`
  display: block;
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.5rem;
`;

// Checkbox can render in red if it has a validation issue
export const Checkbox = styled(({children, invalid, validationText, ...props}) => (
  <div {...props}>
    <label>{children}</label>
    {invalid && validationText ? <CheckboxHelp>{validationText}</CheckboxHelp> : null}
  </div>
))`
  ${props => {
    if (props.invalid) {
      return `
        label {
          color: #dc3545;
        }

        input[type="checkbox"] {
          outline: 1px solid #dc3545;
          outline-offset: 2px;
        }
      `;
    }
  }}
`;

const AdvancedSettingsToggle = styled.div`
  cursor: pointer;
  color: #666;
  font-weight: 500;
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  user-select: none;
  
  &:hover {
    color: #333;
  }
`;

const AdvancedSettingsSection = styled.div`
  padding-left: 1rem;
  border-left: 2px solid #f0f0f0;
  margin-left: 0.5rem;

  > * {
    margin-top: 0.625rem;
  }
`;

const MeasurementUnitRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 1rem;

  > ${Checkbox} {
    margin-bottom: 0.4rem;
  }
`;

const ColorPickerGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: center;

  > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  input[type="color"] {
    width: 60px;
    height: 32px;
    border: 1px solid #000000;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const ColorWarning = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  color: #856404;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const PreviewOrderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export default withUser(QuestionBuilder);